/**
 * NextFollowUpSection — "Próximo seguimiento" del form post-llamada.
 *
 * Regla (del dueño 2026-04-23): obligatorio cuando call_result ∈
 * {answered_interested, no_answer, voicemail}. Opcional para el resto.
 *
 * Forma del payload emitido al padre (via onChange):
 *   {
 *     next_action_type: 'call' | 'whatsapp' | 'email' | 'meeting' | 'reminder',
 *     owner_id: uuid,
 *     sla_deadline: ISO-8601 con tz,
 *     notes?: string,
 *   }
 *
 * Si la sección no está "completa" (faltan campos obligatorios) el
 * callback pasa null — el padre deshabilita el botón Guardar.
 *
 * Backend contrato:
 *   POST /api/crm/calls/{id}/complete con payload.next_follow_up
 *   Ver `app/schemas/call_orchestrator.py::NextFollowUpSpec`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useCRMUsers } from '@/hooks/useCRMUsers';
import { DateInput } from '@/components/ui/DateInput';

export type NextActionType =
  | 'call'
  | 'whatsapp'
  | 'email'
  | 'meeting'
  | 'reminder';

export interface NextFollowUpValue {
  next_action_type: NextActionType;
  owner_id: string;
  sla_deadline: string; // ISO-8601
  notes?: string;
}

interface NextFollowUpSectionProps {
  /** call_result actual — determina obligatoriedad y default suggestions. */
  callResult: string;
  /** Se dispara cada vez que el valor válido cambia. null = incompleto. */
  onChange: (value: NextFollowUpValue | null) => void;
  /** Si el resultado fuerza la sección (visual distinto). */
  required?: boolean;
}

const REQUIRED_RESULTS = new Set([
  'answered_interested',
  'no_answer',
  'voicemail',
]);

const ACTION_CHIPS: { id: NextActionType; label: string; emoji: string }[] = [
  { id: 'call', label: 'Llamar', emoji: '📞' },
  { id: 'whatsapp', label: 'WhatsApp', emoji: '💬' },
  { id: 'email', label: 'Email', emoji: '📧' },
  { id: 'meeting', label: 'Reunión', emoji: '📅' },
  { id: 'reminder', label: 'Recordatorio', emoji: '🔔' },
];

/** Presets temporales. Devuelve ISO-8601 con tz local. */
function presetDatetime(kind: 'tomorrow10' | 'in2h' | 'friday15'): string {
  const now = new Date();
  const d = new Date(now);
  switch (kind) {
    case 'tomorrow10':
      d.setDate(d.getDate() + 1);
      d.setHours(10, 0, 0, 0);
      break;
    case 'in2h':
      d.setHours(d.getHours() + 2, 0, 0, 0);
      break;
    case 'friday15':
      // next Friday at 15:00
      d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7));
      d.setHours(15, 0, 0, 0);
      break;
  }
  return d.toISOString();
}

/**
 * Default SLA deadline for the spec rules:
 * - no_answer / voicemail → tomorrow at 10:00
 * - answered_interested → in 2 days at 10:00
 * - otros → vacío (el agente decide)
 */
function defaultSlaFor(callResult: string): string {
  const d = new Date();
  if (callResult === 'no_answer' || callResult === 'voicemail') {
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  }
  if (callResult === 'answered_interested') {
    d.setDate(d.getDate() + 2);
    d.setHours(10, 0, 0, 0);
    return d.toISOString();
  }
  return '';
}

/** Convierte ISO → string para <input type="datetime-local"> (YYYY-MM-DDTHH:MM). */
function isoToLocal(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  // toISOString() is UTC; datetime-local wants local time without tz.
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function localToISO(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}

export function NextFollowUpSection({
  callResult,
  onChange,
  required,
}: NextFollowUpSectionProps) {
  const { user: currentUser } = useAuth();
  const { users } = useCRMUsers({ onlyResponsibles: true, isActive: true });

  const isRequired = required ?? REQUIRED_RESULTS.has(callResult);

  const [actionType, setActionType] = useState<NextActionType>('call');
  const [ownerId, setOwnerId] = useState<string>(currentUser?.id ?? '');
  const [slaISO, setSlaISO] = useState<string>(() => defaultSlaFor(callResult));
  const [notes, setNotes] = useState<string>('');

  // Re-sync defaults on callResult change (when the agent changes
  // their mind about the outcome mid-form).
  useEffect(() => {
    setSlaISO((prev) => prev || defaultSlaFor(callResult));
  }, [callResult]);

  // Default owner to currentUser the moment we have their id.
  useEffect(() => {
    if (!ownerId && currentUser?.id) setOwnerId(currentUser.id);
  }, [currentUser?.id, ownerId]);

  const value: NextFollowUpValue | null = useMemo(() => {
    if (!actionType || !ownerId || !slaISO) return null;
    return {
      next_action_type: actionType,
      owner_id: ownerId,
      sla_deadline: slaISO,
      notes: notes.trim() || undefined,
    };
  }, [actionType, ownerId, slaISO, notes]);

  useEffect(() => {
    onChange(value);
  }, [value, onChange]);

  return (
    <section
      data-testid="next-follow-up-section"
      className={`rounded-lg border p-3 mt-3 ${
        isRequired
          ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/10'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <header className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">
          ➕ Próximo seguimiento{' '}
          {isRequired && (
            <span className="ml-1 text-[10px] text-amber-700 font-normal">
              (requerido)
            </span>
          )}
        </h3>
      </header>

      {/* Tipo */}
      <div className="mb-2">
        <p className="text-[11px] text-gray-500 mb-1">Tipo</p>
        <div className="flex flex-wrap gap-1">
          {ACTION_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActionType(c.id)}
              aria-pressed={actionType === c.id}
              className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                actionType === c.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Responsable */}
      <div className="mb-2">
        <label
          htmlFor="nfu-owner"
          className="text-[11px] text-gray-500 block mb-1"
        >
          Responsable
        </label>
        <select
          id="nfu-owner"
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
          className="w-full text-sm border rounded px-2 py-1 bg-white dark:bg-gray-900"
        >
          <option value="">Seleccioná…</option>
          {(users ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email}
              {u.id === currentUser?.id ? ' (yo)' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Cuándo */}
      <div className="mb-2">
        <label
          htmlFor="nfu-sla"
          className="text-[11px] text-gray-500 block mb-1"
        >
          Cuándo
        </label>
        <DateInput
          id="nfu-sla"
          type="datetime-local"
          value={isoToLocal(slaISO)}
          onChange={(e) => setSlaISO(localToISO(e.target.value))}
          className="w-full text-sm"
        />
        <div className="flex flex-wrap gap-1 mt-1">
          <button
            type="button"
            onClick={() => setSlaISO(presetDatetime('in2h'))}
            className="text-[11px] text-primary hover:underline"
          >
            En 2h
          </button>
          <span className="text-gray-300">·</span>
          <button
            type="button"
            onClick={() => setSlaISO(presetDatetime('tomorrow10'))}
            className="text-[11px] text-primary hover:underline"
          >
            Mañana 10h
          </button>
          <span className="text-gray-300">·</span>
          <button
            type="button"
            onClick={() => setSlaISO(presetDatetime('friday15'))}
            className="text-[11px] text-primary hover:underline"
          >
            Viernes 15h
          </button>
        </div>
      </div>

      {/* Nota */}
      <div>
        <label
          htmlFor="nfu-notes"
          className="text-[11px] text-gray-500 block mb-1"
        >
          Nota (opcional)
        </label>
        <textarea
          id="nfu-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full text-sm border rounded px-2 py-1 resize-none bg-white dark:bg-gray-900"
          placeholder="Contexto para el próximo contacto…"
        />
      </div>
    </section>
  );
}
